import { getHoursAndMinutes } from '@/data/utils/eddDateCalculation';

/** Wraps to 0-23 for a hour count that has crossed midnight, possibly more than once. */
const normalizeHour = (hours: number): number => ((hours % 24) + 24) % 24;

const formatReadyTime = (hours: number): string =>
	`${String(normalizeHour(hours)).padStart(2, '0')}:00`;

/**
 * PDP delivery-options story, AC7: pickupReadyTime is the query time plus
 * EDD_PICKUP_SLA_HOURS, rounded up to the next whole hour as soon as any minutes
 * are present — 14:00 stays 17:00 with the default 3-hour SLA, 14:01 rounds to
 * 18:00. Evaluated in the same browser/store-timezone resolution story 158621
 * AC25-AC27 already governs (`getHoursAndMinutes`), not re-derived here.
 *
 * AC8 ("when it is not calculated") is the caller's concern: this function always
 * produces a same-day ready time, so it should only be called once the resolved
 * pickup date's descriptor is `'today'`; any other descriptor uses the fixed
 * `EDD_PICKUP_NEXT_DAY_READY_TIME` storeconf value instead, and `available-no-date`
 * gets no ready time at all.
 */
export const calculatePickupReadyTime = (
	slaHours: number,
	pickupNextDayReadyTime: string,
	now: Date = new Date(),
	storeCloseTime?: string
): string => {
	const { hours, minutes } = getHoursAndMinutes(now);
	const resultHours = hours + slaHours;
	const roundedHours = minutes > 0 ? resultHours + 1 : resultHours;
	const [minHours] = pickupNextDayReadyTime.split(':').map(Number);
	const floored = Math.max(roundedHours, minHours);
	const closeHours = storeCloseTime ? Number.parseInt(storeCloseTime, 10) : Number.NaN;
	// Never promise a ready time later than the store's closing hour (24h "HH:MM").
	return formatReadyTime(Number.isFinite(closeHours) ? Math.min(floored, closeHours) : floored);
};
